import { Sparkles, X, CheckCircle2, Crown, Loader2, LogIn } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function PaywallModal({ open, onClose, user, reason }) {
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const isGuest = user?.is_guest;

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await api.post("/stripe/create-checkout-session", {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.requires_sign_in) {
        // Backend told us guest must sign in first
        handleLogin();
        return;
      }
      toast.error("Couldn't start checkout", {
        description: (typeof detail === "string" ? detail : detail?.message) || err?.message || "Try again.",
      });
      setLoading(false);
    }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const used = user?.free_analyses_used ?? 0;
  const limit = user?.free_analyses_limit ?? 3;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
      data-testid="paywall-modal"
    >
      <div className="relative w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-3xl p-7 shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="paywall-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#FF2D55]/10 border border-[#FF2D55]/20 flex items-center justify-center">
            <Crown className="w-5 h-5 text-[#FF2D55]" strokeWidth={2} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-black text-white text-lg tracking-tight">
              KeyFinder Unlimited
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              {reason === "free_limit" ? `you've used ${used} of ${limit} free analyses` : "go unlimited"}
            </span>
          </div>
        </div>

        <div className="bg-[#0F0F13] border border-white/5 rounded-2xl p-5 mb-5 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF2D55]/8 to-transparent pointer-events-none" />
          <div className="relative z-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono font-semibold">
              3-day free trial · then
            </div>
            <div className="font-display text-5xl font-black tracking-tighter text-white led-glow mt-2">
              $2.99<span className="text-2xl text-zinc-400">/mo</span>
            </div>
            <div className="text-[11px] text-zinc-500 font-body mt-1">
              cancel anytime · billed monthly
            </div>
          </div>
        </div>

        <ul className="space-y-2.5 mb-6">
          {[
            "Unlimited key analyses",
            "Camelot notation + relative key + BPM",
            "Personal history of every detection",
            "Cancel anytime, no questions",
          ].map((t, i) => (
            <li key={i} className="flex items-center gap-2.5 text-sm text-zinc-300 font-body">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] shrink-0" strokeWidth={2.2} />
              <span>{t}</span>
            </li>
          ))}
        </ul>

        {isGuest ? (
          <>
            <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-2xl p-3 mb-4 text-[11px] text-[#FFB800] font-body text-center">
              Log in to start your subscription — your free analyses carry over.
            </div>
            <button
              onClick={handleLogin}
              data-testid="paywall-login-button"
              className="w-full bg-[#FF2D55] text-white hover:bg-[#D11B47] font-bold px-8 py-4 rounded-full text-base font-display tracking-tight transition-all shadow-[0_0_24px_rgba(255,45,85,0.35)] hover:shadow-[0_0_36px_rgba(255,45,85,0.6)] flex items-center justify-center gap-2"
            >
              <LogIn className="w-4 h-4" strokeWidth={2.4} />
              Log in to subscribe
            </button>
          </>
        ) : (
          <button
            onClick={handleSubscribe}
            disabled={loading}
            data-testid="subscribe-button"
            className="w-full bg-[#FF2D55] text-white hover:bg-[#D11B47] disabled:opacity-60 disabled:cursor-not-allowed font-bold px-8 py-4 rounded-full text-base font-display tracking-tight transition-all shadow-[0_0_24px_rgba(255,45,85,0.35)] hover:shadow-[0_0_36px_rgba(255,45,85,0.6)] flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecting to Stripe…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" strokeWidth={2.4} />
                Start 3-day free trial
              </>
            )}
          </button>
        )}

        <p className="text-center text-[10px] text-zinc-600 font-body mt-4 leading-relaxed">
          Powered by Stripe. Your card is securely handled by Stripe — KeyFinder never sees it.
        </p>
      </div>
    </div>
  );
}
